import logBase from '../utils/logger'
import { initApsLogic } from './partners/amazon-header-bidding'
import { initPrebid } from './prebid-handler'
import { BIDDERS_TIMEOUT, FAILSAFE_TIMEOUT } from '../constants'
import { BannersConfigWithDefault } from '../configs/game-configs'

const log = logBase.extend('PREBID').extend('Auction')

type BidRequest = Promise<unknown>

/**
 * Starts bidder libraries and runs the first ad auction
 */
export const startAdsAuction = async (adSlots: googletag.Slot[], config: BannersConfigWithDefault) => {
  await Promise.all([initApsLogic(), initPrebid(config)])
  executeParallelAuction(adSlots)
}

/**
 * Runs the first auction with all available banners
 */
const executeParallelAuction = (adSlots: googletag.Slot[]) => {
  executeRequests([apsRequest(adSlots), prebidRequest()])
}

/**
 * Runs a separate auction for the given AdSlots and refreshes them
 */
export const refreshBidAds = (adSlots: googletag.Slot[]) => {
  log('Refreshing bids: ', adSlots)
  window.googletag.cmd.push(() => {
    executeRequests([apsRequest(adSlots), prebidRequest(adSlots)], adSlots)
  })
}

const executeRequests = (bidRequests: BidRequest[], adSlots: googletag.Slot[] = []) => {
  Promise.all(bidRequests)
    .then(() => {
      log('Bids requested successfully')
    })
    .catch((error) => {
      log('Requesting bids error: ', error)
    })
    .finally(() => {
      window.googletag.cmd.push(() => {
        log('Loading new ads...')
        adSlots.length > 0 ? window.googletag.pubads().refresh(adSlots) : window.googletag.pubads().refresh()
      })
    })
}

const apsRequest = (adSlots: googletag.Slot[]): BidRequest => {
  const apsRequestBids = new Promise((resolve, reject) => {
    window.apstag.fetchBids(
      {
        slots: adSlots,
        timeout: BIDDERS_TIMEOUT,
      },
      (bids: unknown) => {
        log('Aps bids', bids)
        window.googletag.cmd.push(() => {
          window.apstag.setDisplayBids()
          resolve(bids)
        })
      }
    )
    setTimeout(() => {
      reject(new Error(`Aps request timeout has exceeded ${FAILSAFE_TIMEOUT}`))
    }, FAILSAFE_TIMEOUT)
  })

  return apsRequestBids
}

const prebidRequest = (adSlots: googletag.Slot[] = []): BidRequest => {
  const adUnitCodes = adSlots.map((adSlot) => adSlot.getAdUnitPath())
  const prebidRequestBids = new Promise<void>((resolve, reject) => {
    window.pbjs.que.push(() => {
      window.pbjs.requestBids({
        timeout: BIDDERS_TIMEOUT,
        adUnitCodes, // If empty all ad units will be affected
        bidsBackHandler: () => {
          window.googletag.cmd.push(() => {
            adUnitCodes.length > 0
              ? window.pbjs.setTargetingForGPTAsync(adUnitCodes)
              : window.pbjs.setTargetingForGPTAsync()
            resolve()
          })
        },
      })
    })

    setTimeout(() => {
      reject(new Error(`Prebid request timeout has exceeded ${FAILSAFE_TIMEOUT}`))
    }, FAILSAFE_TIMEOUT)
  })

  return prebidRequestBids
}
